import React, { useEffect, useMemo, useState } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useDeploymentContent } from "@/companion/DeploymentContentContext";
import axios from "axios";
import { CheckCircle2, Loader2, Upload, AlertTriangle } from "lucide-react";

/**
 * PublicApply — the candidate portal front door.
 *
 * Public, unauthenticated, mobile-first. Welcomes people warmly at the surface
 * and validates seriously underneath. Never reveals internal candidate IDs on
 * success. Resume upload is only shown when the backend reports it enabled.
 */
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABILITY_LABELS = {
  weekday_mornings:   "Weekday mornings",
  weekday_afternoons: "Weekday afternoons",
  weekday_evenings:   "Weekday evenings",
  weekends:           "Weekends",
  flexible:           "Flexible / any time",
};

function Field({ label, hint, required, children, error }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-stone-800">
          {label}{required ? <span className="text-rose-600 ml-0.5" aria-hidden="true">*</span> : null}
        </span>
        {hint ? <span className="text-[11px] text-stone-500">{hint}</span> : null}
      </div>
      <div className="mt-1.5">{children}</div>
      {error ? <div className="text-xs text-rose-600 mt-1" role="alert">{error}</div> : null}
    </label>
  );
}

const inputClass =
  "w-full px-3.5 py-2.5 rounded-xl subtle-border bg-white text-sm text-stone-900 outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-colors";

export default function PublicApply() {
  const companion = useCompanion();
  const deployment = useDeploymentContent();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [boutique, setBoutique] = useState("any");
  const [availability, setAvailability] = useState([]);
  const [about, setAbout] = useState("");
  const [resume, setResume] = useState(null);
  const [consentAck, setConsentAck] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    axios.get(`${BASE_URL}/api/public/apply/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, []);

  const toggleAvailability = (value) => {
    setAvailability((cur) => cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  const orgName = deployment?.organization?.name || "Rainbow Interests";
  const productName = companion?.productWordmark || "";

  const boutiquesByMarket = useMemo(() => {
    const groups = {};
    (config?.boutiques || []).forEach((b) => {
      const m = b.market || "Other";
      (groups[m] ||= []).push(b);
    });
    return groups;
  }, [config]);

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    phone.trim() &&
    role &&
    availability.length > 0 &&
    consentAck &&
    !submitting;

  const submit = async (ev) => {
    ev.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg("");
    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      role,
      boutique_preference: boutique,
      availability,
      about: about.trim(),
      consent_ack: true,
    };
    try {
      const form = new FormData();
      form.append("payload", JSON.stringify(payload));
      if (resume && config?.resume_upload_enabled) {
        form.append("resume", resume);
      }
      await axios.post(`${BASE_URL}/api/public/apply`, form, { timeout: 30000 });
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErrorMsg(detail || "Something went wrong. Please try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="apply-loading">
        <Loader2 size={20} strokeWidth={1.5} className="text-stone-400 animate-spin" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6" data-testid="apply-config-error">
        <div className="max-w-md text-center">
          <AlertTriangle size={20} strokeWidth={1.5} className="text-amber-600 mx-auto" />
          <p className="mt-3 text-sm text-stone-700">
            We can't load the application form right now. Please try again in a few minutes.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="apply-success">
        <header className="bg-white border-b border-stone-100">
          <div className="max-w-2xl mx-auto px-6 py-5 flex items-baseline gap-3">
            <span className="text-2xl font-semibold tracking-tight text-stone-900">{productName}</span>
            <span className="text-[10px] font-medium uppercase text-stone-400 tracking-wider">
              {orgName} · Applications
            </span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-16">
          <div className="bg-white subtle-border rounded-3xl p-8 sm:p-12 text-center card-shadow">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 mx-auto">
              <CheckCircle2 size={28} strokeWidth={1.5} />
            </div>
            <h1 className="font-serif-display text-stone-900 text-3xl sm:text-4xl " style={{ fontWeight: 400 }}>
              Thank you.
            </h1>
            <p className="text-sm text-stone-600 mt-4 max-w-md mx-auto">
              We've received your application. Someone from our recruiting team will reach out to you at the email you provided.
            </p>
            <p className="text-xs text-stone-500 mt-6">
              You can close this window.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="apply-form">
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-baseline gap-3">
          <span className="text-2xl font-semibold tracking-tight text-stone-900">{productName}</span>
          <span className="text-[10px] font-medium uppercase text-stone-400 tracking-wider">
            {orgName} · Applications
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div>
          <h1 className="font-serif-display text-stone-900 text-4xl sm:text-5xl mt-2" style={{ fontWeight: 400 }}>
            Apply
          </h1>
          <p className="text-sm text-stone-600 mt-4 max-w-lg">
            Tell us a little about yourself. Fields marked with an asterisk are required. We only ask what we genuinely need.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6 bg-white subtle-border rounded-3xl p-6 sm:p-8 card-shadow" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" required>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
                maxLength={60}
                className={inputClass}
                data-testid="apply-first-name"
              />
            </Field>
            <Field label="Last name" required>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
                maxLength={60}
                className={inputClass}
                data-testid="apply-last-name"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email" required hint="So we can reach you back">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                maxLength={200}
                inputMode="email"
                className={inputClass}
                data-testid="apply-email"
              />
            </Field>
            <Field label="Phone" required>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                required
                maxLength={20}
                inputMode="tel"
                className={inputClass}
                data-testid="apply-phone"
              />
            </Field>
          </div>

          <Field label="Which role interests you?" required>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Role of interest">
              {config.roles.map((r) => (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => setRole(r.value)}
                  role="radio"
                  aria-checked={role === r.value}
                  data-testid={`apply-role-${r.value}`}
                  className={`text-sm px-4 py-3 rounded-2xl subtle-border text-left transition-colors ${
                    role === r.value ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 hover:bg-stone-50"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Preferred location" hint="Or open to any">
            <select
              value={boutique}
              onChange={(e) => setBoutique(e.target.value)}
              className={inputClass}
              data-testid="apply-boutique"
            >
              <option value={config.any_boutique_value}>Open to any location</option>
              {Object.entries(boutiquesByMarket).map(([market, list]) => (
                <optgroup key={market} label={market}>
                  {list.map((b) => (
                    <option key={b.code} value={b.code}>{b.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <Field label="When are you available to work?" required hint="Choose all that apply">
            <div className="flex flex-wrap gap-2">
              {config.availability.map((v) => {
                const active = availability.includes(v);
                return (
                  <button
                    type="button"
                    key={v}
                    onClick={() => toggleAvailability(v)}
                    aria-pressed={active}
                    data-testid={`apply-availability-${v}`}
                    className={`text-sm px-4 py-2 rounded-full subtle-border transition-colors ${
                      active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 hover:bg-stone-50"
                    }`}
                  >
                    {AVAILABILITY_LABELS[v] || v}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Tell us about yourself" hint="Optional — a couple of sentences is plenty">
            <textarea
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              rows={4}
              maxLength={2000}
              className={inputClass}
              placeholder="Your experience, what draws you to us, anything else you'd like us to know."
              data-testid="apply-about"
            />
            <div className="text-[11px] text-stone-400 mt-1 text-right">{about.length} / 2000</div>
          </Field>

          {config.resume_upload_enabled ? (
            <Field label="Resume" hint="Optional — PDF or DOCX, under 5 MB">
              <label className="flex items-center gap-3 px-4 py-3 rounded-xl subtle-border bg-white cursor-pointer hover:bg-stone-50" data-testid="apply-resume-label">
                <Upload size={16} strokeWidth={1.5} className="text-stone-500" />
                <span className="text-sm text-stone-700 flex-1 truncate">
                  {resume ? resume.name : "Choose a PDF or DOCX file"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="apply-resume"
                />
              </label>
            </Field>
          ) : (
            <div className="text-[11px] text-stone-500 bg-stone-50 rounded-xl px-3 py-2" data-testid="apply-resume-disabled">
              Resume upload isn't accepted at the moment — please submit your application and we'll follow up.
            </div>
          )}

          <div className="pt-2 border-t border-stone-100">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={consentAck}
                onChange={(e) => setConsentAck(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/10"
                data-testid="apply-consent"
              />
              <span className="text-xs text-stone-600">
                I understand that {orgName} will use the information I've shared to evaluate my interest in joining the team. I confirm the information above is accurate to the best of my knowledge.
              </span>
            </label>
          </div>

          {errorMsg && (
            <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 flex items-start gap-2" role="alert" data-testid="apply-error">
              <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-[11px] text-stone-500">
              We'll never sell your information.
            </p>
            <button
              type="submit"
              disabled={!canSubmit}
              className="text-sm px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="apply-submit"
            >
              {submitting && <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />}
              {submitting ? "Sending…" : "Submit application"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
